import { useEffect, useRef, type ChangeEvent } from 'react'

import { BodyM } from '@/components/Typography'

import { VisuallyHidden } from '@/components/VisuallyHidden'

import { getPercentByRange } from '../../utils'

import { LoanInputStyles, LoanStepInputStyles } from './styles'

interface LoanStepProps {
  label: string
  value: number | string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  step: number
  min: number
  max: number
  valueLabel?: string
  legend: {
    min: string
    max: string
  }
  inputName?: string
  labeledByName?: string
  className?: string
}

const LoanStepInput = ({
  className,
  label,
  value,
  onChange,
  legend,
  valueLabel,
  inputName,
  labeledByName,
  ...inputProps
}: LoanStepProps) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (ref?.current) {
      const { value, min, max } = ref.current
      const percent = getPercentByRange({ value, min, max })
      ref.current.style.backgroundSize = `${percent}% 100%`
    }
  }, [value])

  return (
    <LoanStepInputStyles className={className}>
      <VisuallyHidden>{`${label}, minimo ${legend.min}, maximo ${legend.max}`}</VisuallyHidden>
      <BodyM id={labeledByName} aria-hidden className="label" color="dark-high">
        {label}
      </BodyM>
      <label htmlFor={inputName} className="label-value text-color-primary" aria-hidden>
        {valueLabel || value}
      </label>
      <LoanInputStyles
        id={inputName}
        name={inputName}
        aria-labelledby={labeledByName}
        aria-valuetext={valueLabel}
        ref={ref}
        value={value}
        type="range"
        {...inputProps}
        onInput={onChange}
      />
      <div className="input-legend" aria-hidden>
        <BodyM color="dark-high">{legend.min}</BodyM>
        <BodyM color="dark-high">{legend.max}</BodyM>
      </div>
    </LoanStepInputStyles>
  )
}

export default LoanStepInput
